<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, provide, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faRectangleVerticalHistory } from '@fortawesome/duotone-regular-svg-icons'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import Button from '~/components/ui/Button.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCarouselItem from '~/components/ui/AuCarouselItem.vue'
import AuCarousel from '~/components/ui/AuCarousel.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import CardSkeleton from '~/components/skeletons/CardSkeleton.vue'
import CardRenderer from '~/components/cards/CardRenderer.vue'

const props = withDefaults(defineProps<{
  resource: BaseItem
  showLabel?: boolean
}>(), {
  showLabel: false
})

const { t } = useI18n()
const { findChildren } = useContentApi()

const dialogOpen = ref(false)

const { data, isLoading, isError } = useQuery({
  queryKey: ['learning_path_resources', props.resource.mainLocationId],
  queryFn: () => findChildren(
    [props.resource.mainLocationId],
    [ContentType.ProductPart, ContentType.ProductArticle]
  ),
  staleTime: Infinity,
  enabled: computed(() => dialogOpen.value),
})

// Give resource cards information about where they are shown
provide('inStudentActivityDialog', true)
</script>

<template>
  <AuDialog
    v-model="dialogOpen"
    :title="`${resource.title} ${t('learningPath.learningPathParts', { partsCount: data?.length })}`"
    size="xxl"
  >
    <template #trigger>
      <Button
        v-bind="$attrs"
        size="lg"
        :aria-label="!showLabel && t('learningPath.learningPathOverview')"
        @click="dialogOpen = true"
      >
        <font-awesome-icon :icon="faRectangleVerticalHistory" />
        <span v-if="showLabel" v-text="t('preview')" />
      </Button>
    </template>
    <template #content>
      <div
        v-if="isLoading"
        class="w-full px-4"
      >
        <AuCarousel>
          <AuCarouselItem
            v-for="i in 5"
            :key="i"
            class="w-full"
          >
            <CardSkeleton width="215px" />
          </AuCarouselItem>
        </AuCarousel>
      </div>
      <div v-if="isError || !data?.length && !isLoading">
        <AuCallout
          variant="warning"
          :title="t('learningPath.noResourcesFound')"
        />
      </div>
      <div
        v-else
        class="w-full px-4"
      >
        <AuCarousel>
          <AuCarouselItem
            v-for="item in data"
            :key="item.locationId"
          >
            <CardRenderer :resource="item" />
          </AuCarouselItem>
        </AuCarousel>
      </div>
    </template>
  </AuDialog>
</template>
